import React from 'react';
import cn from 'classnames';
import SectionTitle from '../Shared/Tailwind/SectionTitle';
import InputField from '../Shared/Tailwind/InputField';
import { FIELD_TYPE_DATE, FIELD_TYPE_RADIO, FIELD_TYPE_TEXT, FIELD_TYPE_TEXTAREA } from '../../utils/general';
import UploadRequiredDocuments, {
  type ExistingUploadedDocumentsByDocumentId,
  type UploadedDocuments
} from './UploadRequiredDocuments';
import {
  DEFAULT_PAYMENT_TYPE,
  INCENTIVE,
  PAYMENT_TYPES,
  PaymentRequestTypeValue,
  SCHOLARSHIP
} from './paymentRequestTypes';
import { isValidAmountRequested } from '../../utils/validation/award-payment';
import { formatCurrencyAmount } from '../../util/miscUtil';

export type DetailsFormState = {
  paymentRecipient: string;
  amountRequested: string;
  purpose: string;
  awardType: PaymentRequestTypeValue;
  notes: string;
  lastDateOfPayStubs: string;
};

const styles = {
  card: 'w-full self-stretch p-10 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-cyan-600 flex flex-col justify-start items-start',
  stack: 'self-stretch flex flex-col',
  title: 'justify-start text-sky-800 text-3xl font-normal leading-10',
  sectionTop: 'self-stretch mt-4 pt-2 text-left',
  sectionTopLg: 'self-stretch',
  heading: 'justify-start text-blue-950 text-xl font-bold leading-8',
  bodyText: 'justify-start text-neutral-700 text-base font-normal leading-6 text-left',
  label: 'justify-start text-blue-950 text-sm font-semibold leading-5',
  requiredMark: 'text-red-600',
  radioRowWrap: 'self-stretch pt-2 flex flex-col justify-start items-start',
  radioRow: 'self-stretch inline-flex flex-wrap justify-start items-start gap-4',
  radioItem: 'self-stretch inline-flex items-center gap-2',
  radioText: 'text-neutral-700 text-base font-normal leading-6',
  fieldGroup: 'self-stretch pt-5 flex flex-col items-start',
  fieldLabelWrap: 'self-stretch pb-2 text-left',
  fieldLabel: 'text-left text-blue-950 text-sm font-semibold leading-5',
  textInput:
    'w-full h-11 px-3 py-2.5 rounded-sm outline outline-1 outline-offset-[-1px] outline-zinc-200 leading-6 Roboto, Helvetica, Arial, sans-serif rounded-md border border-brand-border outline-none focus:border-brand-border focus:ring-2 focus:ring-brand-border focus:ring-offset-0',
  placeholderText: 'text-left text-neutral-950/50 text-base font-normal',
  textArea: 'w-full min-h-15 px-3 py-2.5 rounded-sm border border-zinc-300 Roboto, Helvetica, Arial, sans-serif',
};

const isPaymentRequestTypeValue = (value: string): value is PaymentRequestTypeValue => PAYMENT_TYPES.some((option) => option.value === value);
const stripCurrencyFormatting = (value: string) => value.replace(/[$,\s]/g, '');

type RequestDetailsProps = {
  onFormChange?: (formState: DetailsFormState) => void;
  onUploadedFilesChange?: (files: UploadedDocuments) => void;
  onExistingUploadedDocumentsChange?: (documents: ExistingUploadedDocumentsByDocumentId) => void;
  onDocumentValidationChange?: (hasError: boolean) => void;
  initialUploadedFiles?: UploadedDocuments;
  initialUploadedDocuments?: ExistingUploadedDocumentsByDocumentId;
  initialValues?: DetailsFormState | null;
  readOnly?: boolean;
  showRequiredErrors?: boolean;
};

const RequestDetails = ({
  onFormChange,
  onUploadedFilesChange,
  onExistingUploadedDocumentsChange,
  onDocumentValidationChange,
  initialUploadedFiles,
  initialUploadedDocuments,
  initialValues = null,
  readOnly = false,
  showRequiredErrors = false
}: RequestDetailsProps) => {
  const [paymentRecipient, setPaymentRecipient] = React.useState(initialValues?.paymentRecipient ?? '');
  const [amountRequested, setAmountRequested] = React.useState(stripCurrencyFormatting(initialValues?.amountRequested ?? ''));
  const [isEditingAmountRequested, setIsEditingAmountRequested] = React.useState(false);
  const [purpose, setPurpose] = React.useState(initialValues?.purpose ?? '');
  const [lastDateOfPayStubs, setLastDateOfPayStubs] = React.useState(initialValues?.lastDateOfPayStubs ?? '');
  const [notes, setNotes] = React.useState(initialValues?.notes ?? '');
  const [awardType, setAwardType] = React.useState<PaymentRequestTypeValue>((initialValues?.awardType as PaymentRequestTypeValue) ?? DEFAULT_PAYMENT_TYPE);
  const onAwardTypeChange = (value: string) => {
    if (!isPaymentRequestTypeValue(value)) {
      throw new Error(`Unsupported payment request type: ${value}`);
    }
    setAwardType(value);
  };

  React.useEffect(() => {
    setPaymentRecipient(initialValues?.paymentRecipient ?? '');
    setAmountRequested(stripCurrencyFormatting(initialValues?.amountRequested ?? ''));
    setPurpose(initialValues?.purpose ?? '');
    setLastDateOfPayStubs(initialValues?.lastDateOfPayStubs ?? '');
    setNotes(initialValues?.notes ?? '');
    setAwardType((initialValues?.awardType as PaymentRequestTypeValue) ?? DEFAULT_PAYMENT_TYPE);
  }, [initialValues]);

  React.useEffect(() => {
    onFormChange?.({
      paymentRecipient,
      amountRequested,
      purpose,
      awardType,
      notes,
      lastDateOfPayStubs,
    });
  }, [paymentRecipient, amountRequested, purpose, awardType, notes, onFormChange, lastDateOfPayStubs]);

  const amountRequestedDisplayValue =
    isEditingAmountRequested || amountRequested === '' ? amountRequested : formatCurrencyAmount(amountRequested);

  return (
    <div className={cn(styles.card)}>
      <SectionTitle className={styles.title}>Request Details</SectionTitle>

      <InputField
        name='awardType'
        label='Type of Payment Request'
        type={FIELD_TYPE_RADIO}
        options={PAYMENT_TYPES}
        value={awardType}
        onChange={onAwardTypeChange}
        required
        disabled={readOnly}
        showRequiredErrors={showRequiredErrors}
        wrapperClassName={styles.sectionTop}
        labelClassName={styles.label}
        requiredMarkClassName={styles.requiredMark}
        groupWrapperClassName={styles.radioRowWrap}
        groupClassName={styles.radioRow}
        radioItemClassName={styles.radioItem}
        radioLabelClassName={styles.radioText}
      />

      <div className={cn(styles.sectionTopLg)}>
        <div className={cn(styles.stack)}>
          {awardType === SCHOLARSHIP && (
            <>
              <InputField
                name='paymentRecipient'
                label='Payable to Institution'
                type={FIELD_TYPE_TEXT}
                value={paymentRecipient}
                onChange={(event) => setPaymentRecipient(event.target.value)}
                disabled={readOnly}
                wrapperClassName={styles.fieldGroup}
                labelClassName={styles.fieldLabel}
                inputClassName={cn(styles.textInput, 'placeholder:text-neutral-950/50')}
                required={true}
                showRequiredErrors={showRequiredErrors}
              />
              <InputField
                name='amountRequested'
                label='Amount Requested'
                type={FIELD_TYPE_TEXT}
                value={amountRequestedDisplayValue}
                onChange={(event) => {
                  const sanitizedValue = stripCurrencyFormatting(event.target.value);
                  if (!isValidAmountRequested(sanitizedValue)) {
                    return;
                  }
                  setAmountRequested(sanitizedValue);
                }}
                onFocus={() => setIsEditingAmountRequested(true)}
                onBlur={() => setIsEditingAmountRequested(false)}
                disabled={readOnly}
                wrapperClassName={styles.fieldGroup}
                labelClassName={styles.fieldLabel}
                inputClassName={styles.textInput}
                required={true}
                showRequiredErrors={showRequiredErrors}
              />
              <InputField
                name='purpose'
                label='Purpose'
                type={FIELD_TYPE_TEXTAREA}
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
                disabled={readOnly}
                placeholder='Describe the purpose of this scholarship request'
                wrapperClassName={styles.fieldGroup}
                labelClassName={styles.fieldLabel}
                inputClassName={styles.textArea}
                required={true}
                showRequiredErrors={showRequiredErrors}
              />
            </>
          )}
          {awardType === INCENTIVE && (
            <InputField
              name='lastDateOfPayStubs'
              label='Last Date of Paystubs'
              type={FIELD_TYPE_DATE}
              value={lastDateOfPayStubs}
              onChange={(event) => setLastDateOfPayStubs(event.target.value)}
              disabled={readOnly}
              placeholder='Enter the last date of paystubs'
              wrapperClassName={styles.fieldGroup}
              labelClassName={styles.fieldLabel}
              inputClassName={cn(styles.textInput, 'placeholder:text-neutral-950/50')}
              required={true}
              showRequiredErrors={showRequiredErrors}
            />
          )}
          <UploadRequiredDocuments
            awardType={awardType}
            onUploadedFilesChange={onUploadedFilesChange}
            onExistingUploadedDocumentsChange={onExistingUploadedDocumentsChange}
            onDocumentValidationChange={onDocumentValidationChange}
            initialUploadedFiles={initialUploadedFiles}
            initialUploadedDocuments={initialUploadedDocuments}
            readOnly={readOnly}
          />
          <InputField
            name='notes'
            label='Notes / Questions'
            type={FIELD_TYPE_TEXTAREA}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={readOnly}
            placeholder='Add any additional notes or questions'
            wrapperClassName={styles.fieldGroup}
            labelClassName={styles.fieldLabel}
            inputClassName={styles.textArea}
          />
        </div>
      </div>
    </div>
  );
};

export default RequestDetails;
